import { useState } from 'react'

import { SelectField } from '@components/molecules/SelectField'
import { FormDialog } from '@components/molecules/FormDialog'

import timeFormatter from '@frontend/utils/timeFormatter'
import { scopedTranslation } from '@utils/I18n'

import { useShiftAuthoring } from './ShiftAuthoringContext'
import { useEditShiftMutation } from './useEditShiftMutation'
import { dayOptionsFor } from './options'

import type { ShiftWithSlots } from '../shifts/types'

const t = scopedTranslation('author_shifts')

const currentDay = (dates: string[], shift: ShiftWithSlots, zone?: string | null): string => {
  const shiftDay = timeFormatter.fromISO(shift.startTime, zone).dateTime
  return dates.find((date) => timeFormatter.fromISO(date, zone).dateTime.hasSame(shiftDay, 'day')) ?? dates[0]
}

type MoveShiftDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  shift: ShiftWithSlots
}

export function MoveShiftDialog({ open, onOpenChange, shift }: MoveShiftDialogProps) {
  const { dates, timeZone, onChange } = useShiftAuthoring()
  const { editShift, isPending } = useEditShiftMutation()
  const [targetDate, setTargetDate] = useState(() => currentDay(dates, shift, timeZone))

  const dayOptions = dayOptionsFor(dates, timeZone)

  const onMove = (event: React.FormEvent) => {
    event.preventDefault()
    editShift({
      id: shift.id,
      shift: {
        roleName: shift.roleName,
        startTime: timeFormatter.combineDateAndTime(
          targetDate,
          timeFormatter.fromISO(shift.startTime, timeZone).to24hours(),
          timeZone
        ),
        endTime: timeFormatter.combineDateAndTime(
          targetDate,
          timeFormatter.fromISO(shift.endTime, timeZone).to24hours(),
          timeZone
        ),
        volunteersRequired: shift.shiftSlots.totalCount,
        draft: shift.draft,
        allowSelfSelect: shift.allowSelfSelect,
      },
      onSuccess: () => {
        onOpenChange(false)
        onChange()
      },
    })
  }

  return (
    <FormDialog.Root open={open} onOpenChange={onOpenChange} size="lg" testId="move-shift-dialog">
      <FormDialog.Title errorsPath="rosterAssignment">{t('move_title')}</FormDialog.Title>
      <FormDialog.Form onSubmit={onMove}>
        <FormDialog.Body>
          <p className="tw:text-sm tw:text-gray-600">
            {t('move_subtitle', {
              role: shift.roleName,
              time: timeFormatter.timeRange(shift.startTime, shift.endTime, timeZone),
            })}
          </p>
          <SelectField
            name="day"
            label={t('day_label')}
            value={targetDate}
            onValueChange={setTargetDate}
            options={dayOptions}
          />
        </FormDialog.Body>
        <FormDialog.Footer submitText={t('move_submit')} isLoading={isPending} />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
